import {
  CardPaymentSelectors,
  getChildState,
  getParentState,
  ParentState,
  RewardsPaymentEventTypes,
  useCheckoutState,
  useCheckoutStateSelector,
  WalletSelectors,
} from "@capone/checkout";
import {
  getConfigTenant,
  isCorpTenant,
  useIsTravelApp,
  getIsEMEligible,
} from "@capone/common";
import { Box, Typography } from "@material-ui/core";
import clsx from "clsx";
import dayjs from "dayjs";
import {
  ActionButton,
  B2BSpinner,
  BackButton,
  getHSPEarnOfferDescription,
  EarnOfferBanner,
  LoadingPopup,
  TripPurposePanel,
  TripDescription,
  NotificationBanner,
  BannerSeverity,
  Icon,
  IconName,
} from "halifax";
import queryStringParser from "query-string";
import React, { useContext } from "react";
import { useDispatch, useSelector } from "react-redux";
import { RouteComponentProps } from "react-router-dom";
import { PackagePricing, RewardsAccount, TravelProductEnum } from "redmond";
import {
  PackagesContactInfo,
  PackagesPriceBreakdown,
  PackagesShopProgressBar,
  PackageTreesCard,
} from "..";
import { fetchCorpUserPassengers } from "../../actions/actions";
import { config } from "../../../../api/config";
import { productToEarnV2 } from "../../../../api/v1/rewards/productToEarn";
import { ClientContext } from "../../../../App";
import {
  AVAILABLE,
  getExperimentVariant,
  PACKAGES_CHECKOUT_OPTIMIZATIONS_V1,
  useExperiments,
  useIsPackagesShoppingApiV2Enabled,
} from "../../../../context/experiments";
import { PATH_BOOK_CONFIRMATION } from "../../../../utils/paths";
import { goToHotelShop } from "../../../hotel-shop/utils/queryStringHelpers";
import { PACKAGES_REWARDS_DISABLED_BANNER_TEXT } from "../../../rewards/components/RewardsBanner/constants";
import { useCorePackagesNavigation } from "../../../../context/CorePackagesNavigationContext";
import { Event, TEvent } from "../../state/events";
import {
  getPackageFlightCardTotal,
  getPackageHotelCardTotal,
} from "../../state/selectors/common";
import { PackagesMachineContext } from "../../state/types";
import { getUserPassengersCallState } from "../../reducer";
import { BookingErrorModal } from "../BookingErrorModal";
import { BookingInProgressModal } from "../BookingInProgressModal";
import { trackHotelShopRedirect } from "../DesktopPackagesBookWorkflow/utils";
import { FlightBookPassengerSelection } from "../FlightBookPassengerSelection";
import { HotelBookPassengerSelection } from "../HotelBookPassengerSelection";
import { MobileTripReview } from "../MobileTripReview";
import { PackagesRewardsAndPayment } from "../RewardsAndPayment";
import { SeatSelection } from "../SeatSelection";
import { CONFIRM_AND_BOOK } from "../textConstants";
import { MobilePackagesBookWorkflowConnectorProps } from "./container";
import "./styles.scss";
import { getLoadingMessage } from "./utils";
import { pollPackageVersion } from "../../../itinerary-review/utils/pollPackageVersion";

export interface IMobilePackagesBookWorkflowProps
  extends MobilePackagesBookWorkflowConnectorProps,
    RouteComponentProps {}

export const MobilePackagesBookWorkflow = ({
  history,
  finalizePackageResponse,
  selectedLodging,
  selectedTrip,
  tripDetails,
  selectedRoom,
  hotelFromDate,
  hotelUntilDate,
  packagePricing,
  rewardsAccounts,
  searchedAdultsCount,
  searchedChildren,
  searchedInfants,
  searchedDestination,
  searchedOrigin,
  searchedDepartureDate,
  searchedReturnDate,
  recommendedFlights,
  opaqueQuoteRequest,
  opaquePackageToken,
  selectedAccount,
  entryPoint,
  packageDiscountInfo,
  selectedRoomRateId,
  isInternationalTrip,
  searchKey,
  selectedRoomV2,
  selectedRoomRateIdV2,
  packagePricingV2,
  packagedRoomComponentV2,
  packagePollState,
}: IMobilePackagesBookWorkflowProps) => {
  const breadcrumbsRef = React.useRef<HTMLDivElement>(null);
  const dispatch = useDispatch();

  const { sessionInfo, isRewardsDisabled } = useContext(ClientContext);

  const [state, send] = useCheckoutState<TEvent, PackagesMachineContext>();
  const coreNav = useCorePackagesNavigation();

  const parentState = getParentState(state.value) as ParentState;
  const childState = getChildState(state.value);

  const expState = useExperiments();

  const isPackagesShoppingApiV2Enabled = useIsPackagesShoppingApiV2Enabled();

  // Under v2, source room/pricing from the GetPackage snapshot (fetched by
  // Review's pollPackageVersion) instead of the v0 handoff/shop-response data.
  const effectiveSelectedRoom = isPackagesShoppingApiV2Enabled
    ? selectedRoomV2
    : selectedRoom;
  const effectiveSelectedRoomRateId = isPackagesShoppingApiV2Enabled
    ? selectedRoomRateIdV2
    : selectedRoomRateId;
  const effectivePackagePricing = isPackagesShoppingApiV2Enabled
    ? packagePricingV2
    : packagePricing;

  // Under v2, Book can be reached directly without ever passing through
  // Review (e.g. desktop's funnel skips the REVIEW step) — Review's mount
  // effect is the only other place that triggers pollPackageVersion, so if
  // nothing has populated the snapshot yet, fetch it here instead of relying
  // on Review having run first. Mirrors Review's own mount effect
  // (itinerary-review/component.tsx) and Desktop Book's identical effect.
  React.useEffect(() => {
    if (!isPackagesShoppingApiV2Enabled) return;
    // Snapshot already present (e.g. the user did pass through Review first)
    // — don't double-fetch.
    if (packagedRoomComponentV2 != null) return;
    if (searchKey == null) return;

    const { packageVersionPollTarget } = queryStringParser.parse(
      window.location.search
    );
    const parsed = Number(packageVersionPollTarget);
    const versionPollTarget = Number.isInteger(parsed) ? parsed : undefined;

    pollPackageVersion({ searchKey, targetVersion: versionPollTarget });
    // Deliberately only re-runs if searchKey/the v2 flag change — re-running
    // on every packagedRoomComponentV2 change would re-fetch after the
    // poll's own dispatch resolves it.
  }, [isPackagesShoppingApiV2Enabled, searchKey]);

  const { isTravelApp } = useIsTravelApp();

  const isPackageCheckoutOptimizationExperiment =
    getExperimentVariant(
      expState.experiments,
      PACKAGES_CHECKOUT_OPTIMIZATIONS_V1,
    ) === AVAILABLE;

  const isCostCoveredBySelectedPaymentMethods = useCheckoutStateSelector(
    CardPaymentSelectors.getIsCostCoveredBySelectedPaymentMethods,
  );

  const isExpenseManagementExperiment =
    getExperimentVariant(expState.experiments, "corp-expense-management") ===
    AVAILABLE;

  const isEMEligible = getIsEMEligible(selectedAccount);

  const flightTotal = useCheckoutStateSelector(getPackageFlightCardTotal);
  const hotelTotal = useCheckoutStateSelector(getPackageHotelCardTotal);
  const offerToApply = useCheckoutStateSelector(
    WalletSelectors.getSelectedOffer,
  );

  const setRewardsAccounts = (accounts: RewardsAccount[]) =>
    send({
      type: RewardsPaymentEventTypes.SET_REWARDS_ACCOUNTS,
      accounts,
    });

  const handleBack = () => {
    send(Event.GO_TO_CARD_PAYMENT);
  };

  const handleContinue = () => {
    send({
      type: Event.SET_INITIAL_BOOK_TIME,
      initialTime: dayjs().toISOString(),
    });
    send(Event.NEXT);
  };

  // initialize checkout context from redux
  React.useEffect(() => {
    // Under v2, only packagedRoomComponentV2 and effectivePackagePricing are
    // required. effectiveSelectedRoom can be undefined even once a room is
    // selected (depends on the BE's shopDetails field, which isn't always
    // populated), but its only consumers are cosmetic and already optional-chain
    // it. Everything else below is v0-handoff-shaped data that's `undefined`
    // under v2 and already handled downstream (optional-chained, or tripDetails
    // guarded separately).
    const isReady = isPackagesShoppingApiV2Enabled
      ? Boolean(
          packagedRoomComponentV2 &&
            effectivePackagePricing &&
            expState.experiments.length
        )
      : Boolean(
          selectedLodging &&
            effectiveSelectedRoom &&
            selectedTrip &&
            opaquePackageToken &&
            opaqueQuoteRequest &&
            tripDetails &&
            hotelFromDate &&
            hotelUntilDate &&
            effectivePackagePricing &&
            searchedDestination &&
            searchedOrigin &&
            searchedDepartureDate &&
            expState.experiments.length
        );

    if (isReady) {
      const {
        selectedLodgingIndex,
        selectedLodgingIsTopSeller,
        selectedLodgingMealPlan,
        selectedLodgingIsSearched,
        lodgingSelectedFromMap,
      } = queryStringParser.parse(window.location.search);

      send({
        type: Event.INITIALIZE_CHECKOUT_STATE,
        payload: {
          selectedLodging: selectedLodging ?? undefined,
          // isReady (checked above) already guarantees effectivePackagePricing
          // is non-null; effectiveSelectedRoom can genuinely still be
          // undefined under v2 (shopDetails gap) — selectedRoom is optional
          // on the payload type for exactly this reason.
          selectedRoom: effectiveSelectedRoom ?? undefined,
          selectedTrip: selectedTrip ?? undefined,
          selectedRoomRateId: effectiveSelectedRoomRateId,
          finalizePackageResponse,
          tripDetails: tripDetails ?? undefined,
          hotelFromDate,
          hotelUntilDate,
          packagePricing: effectivePackagePricing as PackagePricing,
          searchedAdultsCount,
          searchedChildren,
          searchedInfants: searchedInfants.map((infant) => infant.age),
          searchedDestination: searchedDestination ?? undefined,
          searchedDepartureDate: searchedDepartureDate ?? undefined,
          searchedReturnDate,
          searchedOrigin: searchedOrigin ?? undefined,
          benchmarkSliceIds: recommendedFlights ?? [],
          selectedLodgingIndex:
            typeof selectedLodgingIndex === "string"
              ? parseInt(selectedLodgingIndex, 10)
              : undefined,
          selectedAccount,
          entryPoint,
          discountInfo: packageDiscountInfo,
          opaqueFlightRequest: opaquePackageToken?.value,
          opaqueLodgingRequest: opaqueQuoteRequest,
          searchKey,
          experiments: expState.experiments,
          sessionInfo,
          channel: isTravelApp ? "TravelApp" : "Mobile",
          selectedLodgingIsTopSeller: selectedLodgingIsTopSeller === "true",
          selectedLodgingMealPlan:
            typeof selectedLodgingMealPlan === "string"
              ? selectedLodgingMealPlan
              : undefined,
          selectedLodgingIsSearched: selectedLodgingIsSearched === "true",
          lodgingSelectedFromMap: lodgingSelectedFromMap === "true",
        },
      });
      return;
    }

    // Under v2, "not ready yet" might just mean the snapshot fetch (triggered
    // above, or by Review if it ran first) hasn't resolved — don't redirect
    // until the poll has genuinely settled with nothing to show. Under v0
    // there's no equivalent async signal, so fall through to redirect
    // immediately, same as before.
    if (isPackagesShoppingApiV2Enabled && packagePollState !== "settled") {
      return;
    }

    trackHotelShopRedirect(
      opaqueQuoteRequest,
      opaquePackageToken,
      finalizePackageResponse,
      {
        selectedLodging,
        selectedRoom: effectiveSelectedRoom,
        selectedTrip,
        tripDetails,
        hotelFromDate,
        hotelUntilDate,
        packagePricing: effectivePackagePricing,
        searchedDestination,
        searchedOrigin,
        searchedDepartureDate,
      },
    );
    if (coreNav.goToHotelShop != null) {
      coreNav.goToHotelShop();
    } else {
      goToHotelShop({ history });
    }
  }, [
    selectedLodging,
    effectiveSelectedRoom,
    selectedTrip,
    finalizePackageResponse,
    tripDetails,
    hotelFromDate,
    hotelUntilDate,
    effectivePackagePricing,
    searchedAdultsCount,
    searchedChildren,
    searchedInfants,
    searchedDestination,
    searchedDepartureDate,
    searchedReturnDate,
    expState.experiments,
    packagePollState,
  ]);


  React.useEffect(() => {
    // update checkout state with rewards accounts from redux
    setRewardsAccounts(rewardsAccounts);
  }, [rewardsAccounts]);

  React.useEffect(() => {
    // update path so header becomes visible for confirmation
    if (parentState === ParentState.bookingConfirmation) {
      history.replace(
        isTravelApp
          ? `${PATH_BOOK_CONFIRMATION}?isTravelApp=true`
          : PATH_BOOK_CONFIRMATION,
      );
    }
  }, [parentState]);

  React.useEffect(() => {
    send({ type: Event.SET_PLATFORM, platform: "mobile" });
  }, []);

  React.useEffect(() => {
    if (breadcrumbsRef.current && parentState === ParentState.review) {
      // scroll to right of mobile breadcrumbs
      breadcrumbsRef.current.scrollLeft = breadcrumbsRef.current.scrollWidth;
    }
  }, [breadcrumbsRef.current, parentState]);

  React.useEffect(() => {
    if (flightTotal !== undefined && hotelTotal !== undefined) {
      Promise.all(
        rewardsAccounts.map((account) =>
          productToEarnV2({
            account: account.accountReferenceId,
            earnItems: {
              [TravelProductEnum.Flights]: {
                amountUsd: flightTotal,
              },
              [TravelProductEnum.Hotels]: {
                amountUsd: hotelTotal,
                earnOfferDescription: getHSPEarnOfferDescription(offerToApply),
              },
            },
          })
            .then((res) => {
              send({
                type: RewardsPaymentEventTypes.SET_EARN_BY_REWARDS_ACCOUNT_ID,
                accountReferenceId: account.accountReferenceId,
                earnResponse: res,
              });
            })
            .catch((e) =>
              console.warn(
                `Failed to fetch earn for account ${account.accountReferenceId}: ${e}`,
              ),
            ),
        ),
      );
    }
  }, [flightTotal, hotelTotal, offerToApply]);

  const isCorporate = isCorpTenant(getConfigTenant());
  const corpPassengerCallState = useSelector(getUserPassengersCallState);

  React.useEffect(() => {
    if (isCorporate) dispatch(fetchCorpUserPassengers());
  }, [isCorporate]);

  const loadingMessage = getLoadingMessage(
    parentState,
    corpPassengerCallState,
    isCorporate,
    childState || undefined,
  );

  return (
    <Box className="mobile-packages-book-workflow-root">
      {parentState === ParentState.review && (
        <>
          <Box className="packages-book-review-header">
            {isTravelApp ? undefined : (
              <BackButton
                className="packages-book-review-header-back-button"
                onClick={handleBack}
              />
            )}

            {isPackageCheckoutOptimizationExperiment ? (
              <Typography className="header-text">
                {CONFIRM_AND_BOOK}
              </Typography>
            ) : (
              <PackagesPriceBreakdown isMobile dropdown />
            )}
          </Box>

          <PackagesShopProgressBar isMobile innerRef={breadcrumbsRef} />

          <MobileTripReview />
          {getHSPEarnOfferDescription(offerToApply) && (
            <EarnOfferBanner isMobile />
          )}
          {isCorpTenant(config.TENANT) &&
            (isExpenseManagementExperiment ? (
              <TripDescription
                setTripDescription={(tripDescription) => {
                  send({ type: Event.SET_TRIP_DESCRIPTION, tripDescription });
                }}
                isEMEligible={isEMEligible}
              />
            ) : (
              <TripPurposePanel
                isMobile
                setTripPurpose={(tripPurpose) => {
                  send({ type: Event.SET_TRIP_PURPOSE, tripPurpose });
                }}
              />
            ))}

          {isRewardsDisabled && (
            <NotificationBanner
              className="rewards-disabled-banner"
              label={PACKAGES_REWARDS_DISABLED_BANNER_TEXT}
              severity={BannerSeverity.WARNING}
              icon={
                <Icon className="banner-icon" name={IconName.WarningAlert} />
              }
            />
          )}
          <ActionButton
            onClick={handleContinue}
            message={CONFIRM_AND_BOOK}
            className={clsx("packages-mobile-book-button", {
              "cx-optimizations-v1": isPackageCheckoutOptimizationExperiment,
            })}
            disabled={!isCostCoveredBySelectedPaymentMethods}
          />

          <PackageTreesCard />
        </>
      )}

      {parentState === ParentState.passengerInformation && (
        <FlightBookPassengerSelection
          isMobile
          showNonModalContent={false}
          showPassportAwareness={isInternationalTrip}
        />
      )}

      {parentState === ParentState.lodgingPassengerInformation && (
        <HotelBookPassengerSelection isMobile showNonModalContent={false} />
      )}

      {parentState === ParentState.contactInformation && (
        <PackagesContactInfo isMobile contactFormOnly />
      )}

      {parentState === ParentState.seatSelection && <SeatSelection isMobile />}

      {parentState === ParentState.cardPayment && (
        <PackagesRewardsAndPayment isMobile />
      )}

      <LoadingPopup
        classes={["mobile-packages-book-loading-popup"]}
        open={!!loadingMessage}
        message={loadingMessage || ""}
        indicator={B2BSpinner}
        indicatorSize="small"
        verticalAlignment="center"
        fullScreen
      />

      <BookingInProgressModal />
      <BookingErrorModal />
    </Box>
  );
};
